import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { AuthContext } from "../context/Auth/AuthContext";
import api from "../services/api";

const buildAlertFromUsage = (usagePercent) => {
  if (usagePercent >= 100) return "full";
  if (usagePercent >= 90) return "critical";
  if (usagePercent >= 75) return "warning";
  return null;
};

const isDismissed = (companyId, level) => {
  try {
    const key = `diskAlertDismissed:${companyId}:${level}`;
    const dismissedUntil = localStorage.getItem(key);
    if (!dismissedUntil) return false;
    const expiry = parseInt(dismissedUntil, 10);
    if (Date.now() < expiry) return true;
    localStorage.removeItem(key);
    return false;
  } catch {
    return false;
  }
};

const useDiskUsageAlerts = () => {
  const { user, socket } = useContext(AuthContext);
  const [alert, setAlert] = useState(null);
  const alertRef = useRef(null);

  const setAlertIfNotDismissed = useCallback((newAlert) => {
    if (!newAlert || !user?.companyId) return;
    if (isDismissed(user.companyId, newAlert.level)) return;
    alertRef.current = newAlert;
    setAlert(newAlert);
  }, [user?.companyId]);

  useEffect(() => {
    if (!user?.companyId) return;

    let cancelled = false;

    const fetchDiskUsage = async () => {
      try {
        const { data } = await api.get(`/companies/${user.companyId}/disk-usage`);
        if (cancelled) return;

        const usagePercent = data.diskQuota > 0
          ? (data.diskUsage / data.diskQuota) * 100
          : 0;

        const level = buildAlertFromUsage(usagePercent);
        if (level) {
          setAlertIfNotDismissed({
            level,
            usagePercent,
            usedSpace: data.formattedDiskUsage,
            quota: data.formattedDiskQuota,
            message: `${data.formattedDiskUsage} de ${data.formattedDiskQuota} utilizado`,
          });
        }
      } catch {
        // silently ignore
      }
    };

    fetchDiskUsage();

    return () => { cancelled = true; };
  }, [user?.companyId, setAlertIfNotDismissed]);

  useEffect(() => {
    if (!user?.companyId || !socket) return;

    const eventName = `company-${user.companyId}-disk-alert`;

    const handleDiskAlert = (data) => {
      if (!data || typeof data.usagePercent !== "number" || !data.alertLevel) return;

      setAlertIfNotDismissed({
        level: data.alertLevel,
        usagePercent: data.usagePercent,
        usedSpace: data.formattedUsedSpace || "",
        quota: data.formattedQuota || "",
        message: data.message || "",
      });
    };

    socket.on(eventName, handleDiskAlert);

    return () => {
      socket.off(eventName, handleDiskAlert);
    };
  }, [user?.companyId, socket, setAlertIfNotDismissed]);

  const dismissAlert = () => {
    if (!alertRef.current || !user?.companyId) return;

    try {
      const key = `diskAlertDismissed:${user.companyId}:${alertRef.current.level}`;
      const expiry = Date.now() + (60 * 60 * 1000);
      localStorage.setItem(key, expiry.toString());
    } catch {
      // localStorage may be unavailable
    }
    alertRef.current = null;
    setAlert(null);
  };

  return { alert, dismissAlert };
};

export default useDiskUsageAlerts;
